'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { IconX } from '@tabler/icons-react';
import classNames from 'classnames';
import maplibregl, { Marker } from 'maplibre-gl';
import { createRoot, Root } from 'react-dom/client';
import { MantineProvider, Popover, UnstyledButton } from '@mantine/core';
import { useMapContext } from '@/contexts/MapContext';
import { BaseMarkerConfig } from './markerconfig';
import styles from './marker.module.css';

interface MarkerContentProps {
  feature: {
    geometry: { coordinates: any; type: string };
    properties?: Record<string, any>;
  };
  config: BaseMarkerConfig;
  isOccuring: boolean;
}

interface BaseMarkerProps {
  map: maplibregl.Map;
  feature: {
    geometry: { coordinates: any; type: string };
    properties?: Record<string, any>;
  };
  config: BaseMarkerConfig;
  zIndex: string;
}

export const CsoMarkerContent = ({ feature, config, isOccuring }: MarkerContentProps) => {
  const [opened, setOpened] = useState(false);

  const { title, details } = config.getPopoverContent(feature);
  const icon = config.getIcon(feature);

  return (
    <MantineProvider>
      <Popover
        width={400}
        position="top"
        clickOutsideEvents={['mouseup', 'touchend']}
        offset={3}
        opened={opened}
        onChange={setOpened}
        zIndex={15}
      >
        <Popover.Target>
          <UnstyledButton
            className={classNames(
              styles.icon,
              styles.iconButton,
              styles[config.iconClass],
              styles[config.buttonClass],
              {
                [styles['iconButton-flashing']]: isOccuring,
              }
            )}
            onClick={() => setOpened((o: any) => !o)}
          >
            {icon}
          </UnstyledButton>
        </Popover.Target>
        <Popover.Dropdown className={styles.popover}>
          <div className={styles.popoverTitle}>
            <div className={styles.iconAndText}>
              <div className={`${styles.icon} ${styles[config.iconClass]}`}>{icon}</div>
              {title}
            </div>
            <UnstyledButton className={styles.closeButton} onClick={() => setOpened(false)}>
              <IconX aria-label="Close" />
            </UnstyledButton>
          </div>
          <hr className={styles.divider} />
          {details}
        </Popover.Dropdown>
      </Popover>
    </MantineProvider>
  );
};

export function CsoMarkerAndPopover({ map, feature, config, zIndex }: BaseMarkerProps) {
  const markerRef = useRef<Marker>(null);
  const rootRef = useRef<Root>(null);
  const elRef = useRef<HTMLDivElement>(null);
  const { activeCsoEvents } = useMapContext();

  const isOccuring = useMemo(() => {
    return !!feature.properties && activeCsoEvents.includes(feature.properties.objectId);
  }, [activeCsoEvents]);

  useEffect(() => {
    elRef.current = document.createElement('div');
    elRef.current.style.zIndex = zIndex;
    rootRef.current = createRoot(elRef.current);

    const parsedCoordinates = feature.geometry.coordinates.map((coord: string | number) =>
      typeof coord === 'string' ? parseFloat(coord) : coord
    );
    const coordinates: [number, number] = config.getCoordinates(parsedCoordinates).slice(0, 2) as [
      number,
      number,
    ];

    markerRef.current = new maplibregl.Marker({ element: elRef.current })
      .setLngLat(coordinates)
      .addTo(map);

    return () => {
      if (markerRef.current) {
        markerRef.current.remove();
      }
    };
  }, [map, feature]);

  useEffect(() => {
    if (rootRef.current) {
      rootRef.current.render(
        <CsoMarkerContent feature={feature} config={config} isOccuring={isOccuring} />
      );
    }
  }, [feature, config, isOccuring]);

  return null;
}
